import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  Button,
  Chip,
  Grid,
  IconButton,
  TableRow,
  TableCell,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import SortableTable, {
  makeColumnObj
} from "../../../../modules/tables/SortableTable";
import DashboardViewLayout from "../../../../layouts/DashboardViewLayout";
import NewCustomerForm from "../../../../inputs/customers/NewCustomerForm";
import CustomerDetailsSlider from "./CustomerDetailsSlider";
import AvatarGroup from "../../../../atoms/mui/AvatarGroup";

import { useCachedDisplayData } from "../../../../../helpers/firebase";
import { useUpdateVendorRelationshipFields } from "../../../../../helpers/components/vendors";
import { MissingCustomers } from "../../../../layouts/missingSettingsLayouts";
import usePermissions from "../../../../../helpers/hooks/usePermissions";
import { ProductsEmissionsFactorsContextProvider } from "../../../../../helpers/contexts/ProductsEmissionsFactors";

// "Delete Customer" only ever hides this customer from the vendor's own
// Customers list. It must never touch `archived` -- that field is the
// buyer's authoritative signal that the relationship is dead (read by
// detectLowConfidenceNaics, detectEmptyVendors, archiveZeroTxnPairs,
// vendorDeduplication.js, and general buyer-side aggregation). A vendor
// clicking this used to silently kill the buyer's live relationship doc,
// orphaning their transaction tracking for that vendor with no visible sign
// anything had changed.
export const useOnArchiveCustomer = () => {
  const updateVendorRelationshipFields = useUpdateVendorRelationshipFields(
    "customers"
  );

  return async ({ id }) =>
    updateVendorRelationshipFields(id, { hiddenByVendor: true });
};

const buildYearChips = (sharedReports) => {
  if (!sharedReports || !sharedReports.length) {
    return (
      <Typography variant="caption" color="textSecondary">
        None
      </Typography>
    );
  }

  return (
    <Grid container spacing={0.5} wrap="wrap">
      {[...sharedReports]
        .sort((a, b) => a.year - b.year)
        .map(({ year, isFinalized }) => (
          <Grid item key={year}>
            <Chip
              label={year}
              size="small"
              color={isFinalized ? "secondary" : "warning"}
              sx={isFinalized ? { color: "white" } : {}}
            />
          </Grid>
        ))}
    </Grid>
  );
};

const CustomerTableRow = ({ customer, products, onClick, onArchive }) => {
  const { palette } = useTheme();
  const {
    buyerName,
    customerContacts = [],
    sharedReports = [],
    sharedProducts = []
  } = customer;

  const contactMembers = customerContacts.map(({ name, email }) =>
    (name || email || "?")[0].toUpperCase()
  );

  const productMembers = sharedProducts.map(({ productId }, idx) => {
    const name = products.find((p) => p.id === productId)?.name;
    return name ? name[0].toUpperCase() : (idx + 1).toString();
  });

  const productColors = sharedProducts.map(({ isFinalized }) =>
    isFinalized ? palette.secondary.main : palette.warning.main
  );

  return (
    <TableRow hover style={{ cursor: "pointer" }} onClick={onClick}>
      <TableCell>
        <Typography variant="body2">{buyerName}</Typography>
      </TableCell>
      <TableCell>
        {customerContacts.length > 0 ? (
          <AvatarGroup
            members={contactMembers}
            avatarColor={palette.customers.main}
          />
        ) : (
          <Typography variant="caption" color="textSecondary">
            None
          </Typography>
        )}
      </TableCell>
      <TableCell>{buildYearChips(sharedReports)}</TableCell>
      <TableCell>
        {sharedProducts.length > 0 ? (
          <AvatarGroup members={productMembers} memberColors={productColors} />
        ) : (
          <Typography variant="caption" color="textSecondary">
            None
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Tooltip title="Delete Customer">
          <span>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                return onArchive(customer);
              }}
            >
              <FontAwesomeIcon
                icon={faTrash}
                size="sm"
                style={{ color: palette.error.main }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const columns = [
  makeColumnObj("Company Name"),
  makeColumnObj("Contacts"),
  makeColumnObj("Shared Reports"),
  makeColumnObj("Shared Products"),
  makeColumnObj("Actions")
];

const CustomersView = () => {
  const [allCustomers, customersLoading] = useCachedDisplayData("customers");
  const customers = allCustomers.filter(
    ({ archived, hiddenByVendor }) => !archived && !hiddenByVendor
  );
  const archiveCustomer = useOnArchiveCustomer();
  const [products] = useCachedDisplayData("product-components");
  const { canEdit } = usePermissions();

  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [tablePage, setTablePage] = useState(0);

  const history = useHistory();
  const location = useLocation();
  const selectedCustomerId = new URLSearchParams(location.search).get("id");
  const selectedCustomer =
    customers.find((c) => c.id === selectedCustomerId) ?? null;
  const setSelectedCustomer = (customer) =>
    customer
      ? history.replace({ search: `?id=${customer.id}` })
      : history.replace({ search: "" });

  return (
    <>
      {showNewCustomerForm && (
        <NewCustomerForm onClose={() => setShowNewCustomerForm(false)} />
      )}
      {selectedCustomer && (
        <CustomerDetailsSlider
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
        />
      )}
      {customers.length === 0 && !customersLoading ? (
        <MissingCustomers setShowNewCustomerForm={setShowNewCustomerForm} />
      ) : (
        <DashboardViewLayout
          viewLoading={customersLoading}
          title="Customers"
          subtitle="Share your emissions data with your customers"
          type="customers"
          showFilterButton={false}
          primaryAction={
            canEdit && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<GroupAddIcon />}
                onClick={() => setShowNewCustomerForm(true)}
              >
                Add A Customer
              </Button>
            )
          }
          table={
            <SortableTable
              rows={customers}
              columns={columns}
              currentPage={tablePage}
              setCurrentPage={setTablePage}
              rowComponent={(customer) => (
                <CustomerTableRow
                  key={customer.id}
                  customer={customer}
                  products={products}
                  onClick={() => setSelectedCustomer(customer)}
                  onArchive={archiveCustomer}
                />
              )}
            />
          }
        />
      )}
    </>
  );
};

const CustomersViewWithContext = () => (
  <ProductsEmissionsFactorsContextProvider>
    <CustomersView />
  </ProductsEmissionsFactorsContextProvider>
);

export default CustomersViewWithContext;
